import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Camera,
  Mountain,
  Calendar,
  ArrowRight,
  Play,
  Pause,
  Volume2,
  VolumeX,
} from 'lucide-react';

const Home: React.FC = () => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 1000], [0, 300]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);

  const services = [
    {
      icon: <Camera size={48} className="text-primary-orange" />,
      title: 'Şehir Çekimleri',
      description:
        "Ankara'nın en güzel açılarından şehir manzaralarını drone ile çekiyoruz.",
      delay: 0.2,
      gradient: 'from-blue-500 to-purple-600',
    },
    {
      icon: <Mountain size={48} className="text-primary-orange" />,
      title: 'Doğa & Manzara',
      description: 'Doğanın muhteşem güzelliklerini havadan görüntülüyoruz.',
      delay: 0.4,
      gradient: 'from-green-500 to-teal-600',
    },
    {
      icon: <Calendar size={48} className="text-primary-orange" />,
      title: 'Etkinlikler & Özel Günler',
      description: 'Düğün, nişan ve özel etkinliklerinizi unutulmaz kılıyoruz.',
      delay: 0.6,
      gradient: 'from-pink-500 to-red-600',
    },
  ];

  useEffect(() => {
    document.title = 'Ana Sayfa - Yüksekkare';
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section with Video Background */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0 w-full h-full">
          <video
            autoPlay
            loop
            muted={isMuted}
            playsInline
            className="w-full h-full object-cover"
            style={{
              filter: 'brightness(0.7) contrast(1.2)',
            }}
          >
            <source
              src="/assets/videos/drone-landing-video.mp4"
              type="video/mp4"
            />
          </video>

          {/* Overlay with animated gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary-navy/80 via-blue-900/60 to-primary-navy/80">
            <div className="absolute inset-0 bg-black/30"></div>
          </div>
        </div>

        {/* Floating particles effect */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-white/20 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -100, 0],
                opacity: [0, 1, 0],
                scale: [0, 1, 0],
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
        </div>

        {/* Video Controls */}
        <div className="fixed bottom-8 right-8 z-20 flex space-x-4">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => setIsVideoPlaying((prev) => !prev)}
            className="bg-white/20 backdrop-blur-md p-3 rounded-full text-white hover:bg-white/30 transition-all"
          >
            {isVideoPlaying ? <Pause size={20} /> : <Play size={20} />}
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => setIsMuted((prev) => !prev)}
            className="bg-white/20 backdrop-blur-md p-3 rounded-full text-white hover:bg-white/30 transition-all"
          >
            {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
          </motion.button>
        </div>

        {/* Hero Content */}
        <motion.div
          className="relative z-10 text-center text-white px-4"
          style={{ y, opacity }}
        >
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-xl md:text-3xl mb-8 max-w-3xl mx-auto text-gray-200 font-light !m-10"
          >
            <span className="text-primary-orange font-semibold">
              Ankara'nın
            </span>{' '}
            gökyüzünden en güzel açıları.
            <br />
            <span className="text-2xl md:text-4xl font-bold bg-gradient-to-r from-primary-orange to-yellow-400 bg-clip-text text-transparent">
              Profesyonel drone fotoğrafçılığı ve videografi.
            </span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="flex flex-col sm:flex-row gap-6 justify-center items-center"
          >
            <motion.div
              whileHover={{ scale: 1.05, y: -5 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link to="/contact">
                <button className="btn-primary text-lg px-10 py-5 text-xl font-bold shadow-2xl hover:shadow-orange-500/50">
                  <span className="mr-2">🚁</span>
                  Teklif Al
                </button>
              </Link>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.05, y: -5 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link to="/portfolio">
                <button className="btn-secondary text-lg px-10 py-5 text-xl font-bold shadow-2xl hover:shadow-blue-500/50">
                  <span className="mr-2">📸</span>
                  Portfolyo
                </button>
              </Link>
            </motion.div>
          </motion.div>

          {/* Floating stats */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-16 flex justify-center space-x-8 text-center"
          >
            {[
              { number: '500+', label: 'Proje' },
              { number: '5+', label: 'Yıl' },
              { number: '200+', label: 'Müşteri' },
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
                whileHover={{ scale: 1.1 }}
                className="bg-white/10 backdrop-blur-md rounded-2xl p-4 min-w-[120px]"
              >
                <div className="text-2xl md:text-3xl font-bold text-primary-orange">
                  {stat.number}
                </div>
                <div className="text-sm text-gray-300">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* Enhanced Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.5 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="flex flex-col items-center space-y-2"
          >
            <div className="text-white/70 text-sm font-medium">Keşfet</div>
            <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center relative">
              <motion.div
                animate={{ y: [0, 12, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="w-1 h-3 bg-primary-orange rounded-full mt-2"
              />
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Enhanced Services Section */}
      <section className="section-padding bg-gradient-to-br from-gray-50 via-white to-gray-50 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-primary-orange/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-primary-navy/5 rounded-full blur-3xl"></div>
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-block mb-6"
            >
              <div className="bg-gradient-to-r from-primary-orange to-orange-600 text-white px-8 py-3 rounded-full text-sm font-bold">
                HİZMETLERİMİZ
              </div>
            </motion.div>

            <h2 className="text-5xl md:text-6xl font-bold text-primary-navy mb-6">
              Neler Sunuyoruz?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Profesyonel drone teknolojisi ile sunduğumuz kapsamlı hizmetler
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: service.delay }}
                whileHover={{ y: -15, scale: 1.02 }}
                className="group relative"
              >
                <div
                  className={`card text-center relative overflow-hidden bg-gradient-to-br ${service.gradient} text-white transform transition-all duration-500`}
                >
                  {/* Hover effect overlay */}
                  <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                  <div className="relative z-10">
                    <div className="mb-6 flex justify-center">
                      <motion.div
                        whileHover={{ rotate: 360, scale: 1.2 }}
                        transition={{ duration: 0.6 }}
                        className="bg-white/20 p-4 rounded-full"
                      >
                        {service.icon}
                      </motion.div>
                    </div>

                    <h3 className="text-2xl font-bold mb-4 text-white">
                      {service.title}
                    </h3>
                    <p className="text-white/90 mb-6 leading-relaxed">
                      {service.description}
                    </p>

                    <Link to="/services">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="inline-flex items-center space-x-2 bg-white/20 hover:bg-white/30 text-white font-medium px-6 py-3 rounded-lg transition-all duration-300 group-hover:space-x-3"
                      >
                        <span>Detayları Gör</span>
                        <ArrowRight size={16} />
                      </motion.button>
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced CTA Section */}
      <section className="section-padding bg-gradient-to-br from-primary-navy via-blue-900 to-primary-navy text-white relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0">
          <motion.div
            animate={{
              rotate: 360,
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: 'linear',
            }}
            className="absolute top-10 left-10 w-32 h-32 border border-white/10 rounded-full"
          />
          <motion.div
            animate={{
              rotate: -360,
              scale: [1, 0.9, 1],
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: 'linear',
            }}
            className="absolute bottom-10 right-10 w-24 h-24 border border-white/10 rounded-full"
          />
        </div>

        <div className="container-custom text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.h2
              className="text-5xl md:text-7xl font-bold mb-8"
              animate={{
                textShadow: [
                  '0 0 20px rgba(255,107,53,0.5)',
                  '0 0 40px rgba(255,107,53,0.8)',
                  '0 0 20px rgba(255,107,53,0.5)',
                ],
              }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              Projenizi Hayata Geçirelim
            </motion.h2>

            <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed">
              Hayalinizdeki çekimi gerçekleştirmek için bizimle iletişime geçin.
              <span className="text-primary-orange font-semibold">
                {' '}
                Ücretsiz danışmanlık
              </span>{' '}
              hizmeti sunuyoruz.
            </p>

            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link to="/contact">
                <button className="btn-primary text-xl px-12 py-6 font-bold shadow-2xl hover:shadow-orange-500/50 transform hover:-translate-y-1 transition-all duration-300">
                  <span className="mr-3">🚀</span>
                  Hemen Başlayın
                </button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
